/**
 * Operator fulfilment location choice. Canonical Tradivo location ids only —
 * never provider merchant-location keys.
 *
 * sales_orders.location_id is an optional import hint. Marketplace orders may
 * arrive with it NULL. Fulfilment authority is the operator-selected location
 * passed to fulfil_sales_order and stored on fulfilments.location_id — the
 * same id used for stock validation and inventory mutation.
 */

import { remainingFulfilmentQty } from "./sales-orders";

export type LocationStockOption = {
  id: string;
  name: string;
};

export type LocationRecommendation = {
  locationId: string | null;
  reason: "preferred" | "sufficient" | "best_available" | "none";
};

export function recommendFulfilmentLocation(opts: {
  locations: LocationStockOption[];
  availableByLocationId: Record<string, number>;
  qtyNeeded: number;
  preferredLocationId?: string | null;
}): LocationRecommendation {
  const qty = Math.max(0, Math.trunc(opts.qtyNeeded));
  const stocked = opts.locations
    .map((l) => ({ id: l.id, available: Math.max(0, opts.availableByLocationId[l.id] ?? 0) }))
    .filter((l) => l.available > 0)
    .sort((a, b) => b.available - a.available);

  if (stocked.length === 0) return { locationId: null, reason: "none" };

  const preferred = (opts.preferredLocationId ?? "").trim();
  const preferredRow = preferred ? stocked.find((l) => l.id === preferred) : undefined;
  if (preferredRow && (qty <= 0 || preferredRow.available >= qty)) {
    return { locationId: preferredRow.id, reason: "preferred" };
  }

  const enough = qty > 0 ? stocked.find((l) => l.available >= qty) : undefined;
  if (enough) return { locationId: enough.id, reason: "sufficient" };

  return { locationId: stocked[0].id, reason: "best_available" };
}

export function balanceKey(productId: string, variantId: string | null | undefined, locationId: string): string {
  return `${productId}::${variantId ?? ""}::${locationId}`;
}

export function availableForExactVariantLocation(
  balances: Record<string, { available: number }>,
  productId: string,
  variantId: string | null | undefined,
  locationId: string,
): number {
  if (!locationId) return 0;
  return Math.max(0, balances[balanceKey(productId, variantId ?? null, locationId)]?.available ?? 0);
}

export function resolveSelectedFulfilmentLocation(opts: {
  selectedLocationId?: string | null;
  orderLocationId?: string | null;
}): string | null {
  const selected = (opts.selectedLocationId ?? "").trim();
  if (selected) return selected;
  const orderLoc = (opts.orderLocationId ?? "").trim();
  return orderLoc || null;
}

export function assertRequestedQtyAgainstAvailable(opts: {
  requestedQty: number;
  available: number;
}): { ok: true } | { ok: false; code: "insufficient_stock" } {
  const requested = Math.max(0, Math.trunc(opts.requestedQty));
  const available = Math.max(0, Math.trunc(opts.available));
  if (requested <= 0) return { ok: true };
  if (available < requested) return { ok: false, code: "insufficient_stock" };
  return { ok: true };
}

export function shouldLoadOrderAvailability(_orderLocationId: string | null | undefined): boolean {
  return true;
}

export function fulfilmentFromOptionLabel(name: string, available: number, recommended: boolean): string {
  return `${name} · ${available} available${recommended ? " · recommended" : ""}`;
}

export type FulfilableLine = {
  id: string;
  product_id: string;
  variant_id: string | null;
  is_bundle?: boolean | null;
  qty_ordered: number;
  qty_fulfilled: number;
  qty_cancelled?: number | null;
};

export function seedLineFulfilmentLocations(opts: {
  items: FulfilableLine[];
  locations: LocationStockOption[];
  balances: Record<string, { available: number }>;
  orderLocationId?: string | null;
}): { qtyByLineId: Record<string, number>; locByLineId: Record<string, string> } {
  const qtyByLineId: Record<string, number> = {};
  const locByLineId: Record<string, string> = {};
  for (const it of opts.items) {
    if (it.is_bundle) continue;
    const remaining = remainingFulfilmentQty(it.qty_ordered, it.qty_cancelled ?? 0, it.qty_fulfilled);
    if (remaining > 0) qtyByLineId[it.id] = remaining;
    const availableByLocationId: Record<string, number> = {};
    for (const l of opts.locations) {
      availableByLocationId[l.id] = availableForExactVariantLocation(
        opts.balances,
        it.product_id,
        it.variant_id,
        l.id,
      );
    }
    const rec = recommendFulfilmentLocation({
      locations: opts.locations,
      availableByLocationId,
      qtyNeeded: remaining,
      preferredLocationId: opts.orderLocationId ?? null,
    });
    if (rec.locationId) locByLineId[it.id] = rec.locationId;
  }
  return { qtyByLineId, locByLineId };
}

export function fulfilmentConfirmDisabled(opts: {
  saving: boolean;
  availabilityReady: boolean;
  items: Array<{
    id: string;
    product_id: string;
    variant_id: string | null;
    is_bundle?: boolean | null;
  }>;
  qtyByLineId: Record<string, number>;
  locByLineId: Record<string, string | undefined>;
  orderLocationId: string | null | undefined;
  balances: Record<string, { available: number }>;
}): boolean {
  if (opts.saving || !opts.availabilityReady) return true;
  return buildFulfilmentRequest({
    items: opts.items,
    qtyByLineId: opts.qtyByLineId,
    locByLineId: opts.locByLineId,
    orderLocationId: opts.orderLocationId,
    balances: opts.balances,
  }).ok === false;
}

export type FulfilmentLineDraft = {
  product_id: string;
  variant_id: string | null;
  qty: number;
  location_id: string;
};

export type BuildFulfilmentRequestResult =
  | { ok: true; lines: FulfilmentLineDraft[]; fallbackLocationId: string }
  | { ok: false; code: "no_qty" | "no_location" | "insufficient_stock" };

/** Bind operator-selected location into both per-line payload and RPC fallback. */
export function buildFulfilmentRequest(opts: {
  items: Array<{
    id: string;
    product_id: string;
    variant_id: string | null;
    is_bundle?: boolean;
  }>;
  qtyByLineId: Record<string, number>;
  locByLineId: Record<string, string | undefined>;
  orderLocationId: string | null | undefined;
  balances: Record<string, { available: number }>;
}): BuildFulfilmentRequestResult {
  const lines: FulfilmentLineDraft[] = [];
  for (const it of opts.items) {
    if (it.is_bundle) continue;
    const qty = Math.max(0, Math.trunc(opts.qtyByLineId[it.id] ?? 0));
    if (qty <= 0) continue;
    const locationId = resolveSelectedFulfilmentLocation({
      selectedLocationId: opts.locByLineId[it.id],
      orderLocationId: opts.orderLocationId,
    });
    if (!locationId) return { ok: false, code: "no_location" };
    const available = availableForExactVariantLocation(
      opts.balances,
      it.product_id,
      it.variant_id,
      locationId,
    );
    if (!assertRequestedQtyAgainstAvailable({ requestedQty: qty, available }).ok) {
      return { ok: false, code: "insufficient_stock" };
    }
    lines.push({
      product_id: it.product_id,
      variant_id: it.variant_id,
      qty,
      location_id: locationId,
    });
  }
  if (lines.length === 0) return { ok: false, code: "no_qty" };
  return { ok: true, lines, fallbackLocationId: lines[0].location_id };
}
